import { supabase } from '@/lib/supabaseClient';

interface DomainTenant {
  tenantId: string;
  tenantName: string;
  theme?: any;
  slug?: string;
}

// Cache for domain tenant lookups to avoid repeated requests
const domainCache = new Map<string, {
  data: DomainTenant | null;
  timestamp: number;
}>();

const CACHE_DURATION = 5 * 60 * 1000; // 5 minutes

export const getDomainTenant = async (): Promise<DomainTenant | null> => {
  // Include port for localhost development
  const hostname = window.location.hostname;
  const port = window.location.port;
  const fullDomain = port ? `${hostname}:${port}` : hostname;

  // Skip domain lookup for development/tunnel URLs
  if (hostname === 'localhost' || hostname.includes('trycloudflare.com')) {
    return null;
  }

  try {
    // Query database for domain mapping
    const { data, error } = await supabase
      .from('tenant_domains')
      .select(`
        tenant_id,
        tenants (
          company_name,
          slug
        )
      `)
      .eq('domain', fullDomain)
      .eq('is_active', true)
      .single();
    
    if (data && !error && data.tenants) {
      const result: DomainTenant = {
        tenantId: data.tenant_id,
        tenantName: (data.tenants as any).company_name,
        slug: (data.tenants as any).slug
      };
      
      // Get theme settings for this tenant
      try {
        const { data: themeData, error: themeError } = await supabase
          .from('tenant_theme_settings')
          .select('current_theme, custom_logo, custom_favicon, custom_seo, custom_themes, sidebar_color_override')
          .eq('tenant_id', data.tenant_id)
          .single();
        
        if (themeData && !themeError) {
          result.theme = {
            currentTheme: themeData.current_theme,
            customLogo: themeData.custom_logo,
            customFavicon: themeData.custom_favicon,
            customSEO: themeData.custom_seo,
            customThemes: themeData.custom_themes || [],
            sidebarColorOverride: themeData.sidebar_color_override
          };
        }
      } catch (themeError) {
        console.warn('[DomainService] Exception fetching theme for tenant:', themeError);
        // Continue without theme - will use defaults
      }
      
      return result;
    }
  } catch (error) {
    console.error('Error fetching domain tenant:', error);
  }
  
  // Try subdomain detection as fallback
  return await detectTenantFromSubdomain();
};

const detectTenantFromSubdomain = async (): Promise<DomainTenant | null> => {
  const hostname = window.location.hostname;
  const parts = hostname.split('.');
  
  // Check for tenant.joulescrm.com pattern
  if (parts.length >= 3 && parts[1] === 'joulescrm') {
    const subdomain = parts[0];
    
    try {
      const { data, error } = await supabase
        .from('tenants')
        .select('tenant_id, company_name, slug')
        .eq('slug', subdomain)
        .single();
      
      if (data && !error) {
        const result: DomainTenant = {
          tenantId: data.tenant_id,
          tenantName: data.company_name,
          slug: data.slug
        };
        
        // Get theme settings for subdomain tenant
        try {
          const { data: themeData } = await supabase
            .from('tenant_theme_settings')
            .select('current_theme, custom_logo, custom_favicon, custom_seo, custom_themes, sidebar_color_override')
            .eq('tenant_id', data.tenant_id)
            .single();
          
          if (themeData) {
            result.theme = {
              currentTheme: themeData.current_theme,
              customLogo: themeData.custom_logo,
              customFavicon: themeData.custom_favicon,
              customSEO: themeData.custom_seo,
              customThemes: themeData.custom_themes || [],
              sidebarColorOverride: themeData.sidebar_color_override
            };
          }
        } catch (themeError) {
          console.warn('Could not fetch theme for subdomain tenant:', themeError);
        }
        
        return result;
      }
    } catch (error) {
      console.error('Error fetching subdomain tenant:', error);
    }
  }
  
  return null;
};

export const getDefaultTenant = (): DomainTenant => {
  return {
    tenantId: '511f29e8-bef3-4065-9927-777d6eafe7f1', // Joules default
    tenantName: 'Joules',
    slug: 'joules'
  };
};

// Clear domain cache when needed (e.g., on logout or tenant switch)
export const clearDomainCache = () => {
  domainCache.clear();
  // Clear session storage cache too
  const hostname = window.location.hostname;
  const port = window.location.port;
  const fullDomain = port ? `${hostname}:${port}` : hostname;
  sessionStorage.removeItem(`tenant_${fullDomain}`);
};